import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuthGate } from '@/context/AuthGateContext';
import { useAuth, useLastAuthEvent } from '@/hooks/useAuth';
import { activateTrial } from '@/lib/trialActivation';
import { useToast } from '@/hooks/use-toast';

export function AuthGateModal() {
  const { isOpen, reason, step, closeGate, advanceToTrial, openGate } = useAuthGate();
  const { login, signup, user, isAuthenticated } = useAuth();
  const lastEvent = useLastAuthEvent();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [mode, setMode] = useState<'signup' | 'login'>('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // After SIGNED_IN (covers post-signup auto-login in Supabase v2 which removed SIGNED_UP),
  // advance to trial step. Also handles email-confirmation recovery: when the user clicks the
  // confirmation link and lands back, React state is reset but sessionStorage survives.
  useEffect(() => {
    if (lastEvent !== 'SIGNED_IN' || !isAuthenticated) return;
    if (step === 'auth' && isOpen) {
      // Normal signup (auto-confirm): advance to trial within the open gate.
      advanceToTrial();
    } else if (sessionStorage.getItem('pendingEmailConfirmation') === 'true') {
      // Email-confirmation flow: user confirmed and navigated back. Reopen the
      // gate at the trial step so they see the trial CTA.
      sessionStorage.removeItem('pendingEmailConfirmation');
      openGate('Confirm your account');
      advanceToTrial();
    }
  }, [lastEvent, isAuthenticated, step, isOpen, advanceToTrial, openGate]);

  const handleAuth = async () => {
    setIsSubmitting(true);
    try {
      if (mode === 'signup') {
        const result = await signup(email, password, {
          first_name: firstName,
          last_name: lastName,
          business_name: businessName,
        });
        if (result.requiresConfirmation) {
          sessionStorage.setItem('pendingEmailConfirmation', 'true');
          toast({ title: 'Check your email', description: 'Confirm your account to continue.' });
          closeGate();
          return;
        }
        // SIGNED_UP event from onAuthStateChange will trigger advanceToTrial via useEffect
      } else {
        await login(email, password);
        closeGate(); // Login: close immediately, no trial step
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Auth failed',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartTrial = async () => {
    if (!user) return;
    setIsSubmitting(true);
    try {
      await activateTrial();
      // Refetch entitlement so gated UI (useEntitlement consumers) unlocks
      // immediately — without this the user stayed gated until a full reload.
      await queryClient.invalidateQueries({ queryKey: ['entitlement'] });
      toast({ title: 'Free trial started!', description: '7 days of Pro features activated.' });
      closeGate();
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Could not activate trial',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Drawer
      open={isOpen}
      onOpenChange={(v) => {
        if (!v) closeGate();
      }}
    >
      <DrawerContent>
        {step === 'auth' ? (
          <>
            <DrawerHeader>
              <div className="flex items-center justify-between">
                <DrawerTitle>Sign up to {reason ?? 'continue'}</DrawerTitle>
                <DrawerClose className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
                  <X className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </DrawerClose>
              </div>
              <DrawerDescription>
                {mode === 'signup' ? 'Create your free account.' : 'Welcome back.'}
              </DrawerDescription>
            </DrawerHeader>
            <div className="space-y-3 p-4">
              {mode === 'signup' && (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label htmlFor="gate-firstname">First name</Label>
                      <Input
                        id="gate-firstname"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="gate-lastname">Last name</Label>
                      <Input
                        id="gate-lastname"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="gate-business">Business name</Label>
                    <Input
                      id="gate-business"
                      value={businessName}
                      onChange={(e) => setBusinessName(e.target.value)}
                    />
                  </div>
                </>
              )}
              <div>
                <Label htmlFor="gate-email">Email</Label>
                <Input
                  id="gate-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="gate-password">Password</Label>
                <Input
                  id="gate-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              <Button className="w-full" onClick={handleAuth} disabled={isSubmitting}>
                {isSubmitting ? 'Please wait…' : mode === 'signup' ? 'Create account' : 'Sign in'}
              </Button>
              <Button
                variant="ghost"
                className="w-full"
                onClick={() => setMode((m) => (m === 'signup' ? 'login' : 'signup'))}
              >
                {mode === 'signup'
                  ? 'Already have an account? Sign in'
                  : 'Need an account? Sign up'}
              </Button>
              <Button variant="ghost" className="w-full" onClick={closeGate}>
                Maybe later
              </Button>
            </div>
          </>
        ) : (
          <>
            <DrawerHeader>
              <div className="flex items-center justify-between">
                <DrawerTitle>Welcome to For Visibility!</DrawerTitle>
                <DrawerClose className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
                  <X className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </DrawerClose>
              </div>
              <DrawerDescription>
                Start with a 7-day free trial of Pro features, or explore on the free tier.
              </DrawerDescription>
            </DrawerHeader>
            <div className="space-y-3 p-4">
              <Button className="w-full" onClick={handleStartTrial} disabled={isSubmitting}>
                {isSubmitting ? 'Activating…' : 'Start 7-day free trial'}
              </Button>
              <p className="text-center text-xs text-muted-foreground">
                Includes all Pro features. No credit card required.
              </p>
              <Button variant="outline" className="w-full" onClick={closeGate}>
                Stay on free tier
              </Button>
            </div>
          </>
        )}
      </DrawerContent>
    </Drawer>
  );
}
